import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Container from '@/components/base/Container';
import Eyebrow from '@/components/base/Eyebrow';
import { services } from '@/mocks/services';
import { reviews } from '@/mocks/reviews';
import TestimonialsCalendar from './TestimonialsCalendar';

const featured = reviews.slice(0, 4);

const scenes = [
  { src: 'https://public.readdy.ai/ai/img_res/sandbox/f2895d2f16dd8702941ce32547f07592/1802b3df-061e-4996-b201-639cd7eafb1d_mw-t-kitchen.webp', alt: 'Calm modern kitchen after an appliance repair' },
  { src: 'https://public.readdy.ai/ai/img_res/sandbox/f2895d2f16dd8702941ce32547f07592/1802b3df-061e-4996-b201-639cd7eafb1d_mw-t-laundry.webp', alt: 'Bright laundry room with a working washer and dryer' },
  { src: 'https://public.readdy.ai/ai/img_res/sandbox/f2895d2f16dd8702941ce32547f07592/1802b3df-061e-4996-b201-639cd7eafb1d_mw-t-dish.webp', alt: 'Dishwasher full of clean plates after repair' },
  { src: 'https://public.readdy.ai/ai/img_res/sandbox/f2895d2f16dd8702941ce32547f07592/1802b3df-061e-4996-b201-639cd7eafb1d_mw-t-oven.webp', alt: 'Modern kitchen with a working built-in oven' },
];

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const [shown, setShown] = useState(0);
  const [visible, setVisible] = useState(true);
  const timer = useRef<number>();
  const item = featured[shown];
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const category = services.find((s) => s.slug === item.serviceSlug)?.name ?? '';
  const anySample = featured.some((r) => r.isSample);
  const go = (d: number) => {
    const next = (index + d + featured.length) % featured.length;
    setIndex(next);
    setVisible(false);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setShown(next);
      setVisible(true);
    }, 320);
  };

  return (
    <>
    <div className="hidden lg:block lg:motion-reduce:hidden">
      <TestimonialsCalendar items={featured} scenes={scenes} anySample={anySample} />
    </div>
    <section className="bg-background-100 py-20 md:py-32 lg:hidden lg:motion-reduce:block">
      <Container>
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16 items-stretch">
          <div className="relative h-[300px] sm:h-[420px] lg:h-[560px] w-full overflow-hidden rounded-xl bg-background-200">
            {scenes.map((s, i) => (
              <img
                key={s.src}
                src={s.src}
                alt={s.alt}
                title="Customer experiences with MENDWELL"
                width={1000}
                height={1100}
                loading="eager"
                decoding="async"
                aria-hidden={i !== index}
                className={`absolute inset-0 h-full w-full object-cover object-top transition-[opacity,transform] duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${i === index ? 'opacity-100 scale-100' : 'opacity-0 scale-[1.06]'}`}
              />
            ))}
          </div>
          <div className="flex flex-col">
            <Eyebrow label="Customer Experiences" />
            <h2 className="mt-6 text-[34px] md:text-[48px] font-medium leading-[1.05] tracking-[-0.025em] text-foreground-950">
              What a Good Visit Feels Like.
            </h2>
            {anySample && (
              <p className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-accent-100 px-3 py-1.5 text-xs text-accent-900">
                <i className="ri-information-line" />
                Sample reviews for design preview. Not verified customer feedback.
              </p>
            )}
            <blockquote
              className={`mt-10 lg:mt-auto transition-[opacity,transform,filter] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${visible ? 'opacity-100 translate-y-0 blur-0 duration-700' : 'opacity-0 -translate-y-3 blur-[2px] duration-300'}`}
              aria-live="polite"
            >
              {category && (
                <p className="text-[11px] uppercase tracking-[0.16em] text-primary-600">{category}</p>
              )}
              <p className="mt-4 text-xl md:text-[26px] font-light leading-snug tracking-tight text-foreground-950">&ldquo;{item.text}&rdquo;</p>
              <footer className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs uppercase tracking-[0.14em] text-foreground-600">
                <span>{item.displayName}</span>
                {typeof item.rating === 'number' && (
                  <span className="flex items-center gap-0.5 text-primary-500" aria-label={`Rated ${item.rating} out of 5`}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={i < item.rating ? 'ri-star-fill' : 'ri-star-line text-background-400'} />
                    ))}
                  </span>
                )}
              </footer>
            </blockquote>
            <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-background-300/60 pt-6">
              <div className="flex items-center gap-6">
                <span className="text-sm text-foreground-600">{`0${index + 1} / 0${featured.length}`}</span>
                <Link to="/reviews" className="inline-flex items-center gap-2 whitespace-nowrap text-sm text-foreground-950 mw-link">
                  View All Reviews <span className="mw-arrow">&rarr;</span>
                </Link>
              </div>
              <div className="flex gap-2">
                <button type="button" aria-label="Previous" onClick={() => go(-1)} className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-950 hover:border-primary-400">
                  <i className="ri-arrow-left-line" />
                </button>
                <button type="button" aria-label="Next" onClick={() => go(1)} className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-white hover:bg-primary-600">
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
    </>
  );
}